import { IModel, IViolation, Collection, IAttribute } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';

import { Activity } from 'app/model/pipeline/activity';
import { DataFlow } from 'app/model/dataflow/dataflow';
import {
    IntegrationRuntime,
    IValidationContext,
    LinkedService,
    ModelConstants,
    ParameterValue,
    ValidationConstants
} from 'shell-models-lib';
import { format, sanitizeAndFormatString, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { AzureSqlDWTableDataset } from 'app/model/dataset/azure-sql-dw-table.dataset';
import { validateEntityParameters } from 'app/shared/utils/activity-validation.util';
import { checkForEmptyValuetypes, getDataFlowBaseArguments } from 'app/shared/components/parameterizable/parameterizable-dataflow-utilities';
import { ComputeType, TraceLevelType } from 'app/shared/constants/dataflow-runtime.options';
import { DataFlowTypesUtils } from '../dataflow/dataflow-type-utilities';
import { DataFlowParameter } from '../dataflow/dataflow-parameter';
import { getDataFlowIrSparkGatewayErrorMessage, isDataflowIRReadyModelValidator } from 'app/integrationruntime/ir-service/ir-substatus-validation.utils';
import { validateIRConstraints, validateStagingLinkedService } from './execute-dataflow.util';
import { SynapseAnalyticsSettings } from '../dataflow/dataset-settings/synapse-analytics-settings';
import { HiveSettings } from '../dataflow/dataset-settings/hive-dataset-settings';
import { FactoryCacheService } from 'app/services/factory-cache.service';
import { formatRegionString } from 'app/shared/utils/region.util';
import { getAllExistingCheckpoint } from 'app/authoring/shared/authoring.utils';
import { isSapODataSource, isSapOdpSource, isSourceTypeEq } from 'app/model/dataflow/dataflow-utils';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { ModelService } from '../services/model.service';
import { WarehouseSettings } from '../dataflow/dataset-settings/warehouse-settings';

@Metamodel.model({
    name: ModelConstants.executeDataFlowActivity,
    display: [
        format(stringResources.ModelName_ActivityTemplate, stringResources.DataFlow),
        format(stringResources.ModelName_ActivitiesTemplate, stringResources.DataFlow)
    ],
    referable: true,
    validator: ExecuteDataFlowActivity.validateExecuteActivity
})
export class ExecuteDataFlowActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly STAGING_LINKED_SERVICE_PARAMETERS: IAttribute;
    public static readonly STAGING_STORAGE_PATH: IAttribute;
    public static readonly COMPUTE_TYPE: IAttribute;
    public static readonly CORE_COUNT: IAttribute;
    public static readonly TRACE_LEVEL: IAttribute;
    public static readonly FIRST_ROW_ONLY: IAttribute;
    public static readonly RUN_CONCURRENTLY: IAttribute;
    public static readonly SOURCE_STAGING_CONCURRENCY: IAttribute;
    public static readonly CONTINUE_ON_ERROR: IAttribute;
    public static readonly ALLOW_CACHING: IAttribute;
    public static readonly DATAFLOW_DATASET_PARAMETERS: IAttribute;
    public static readonly CUSTOMIZED_CHECKPOINT_KEY: IAttribute;

    @Metamodel.attribute({
        display: stringResources.IntegrationRuntime,
        reference: true,
        type: IntegrationRuntime.MODEL,
        constraint: {
            validator: ExecuteDataFlowActivity.validateIRConstraints
        }
    })
    public integrationRuntime: IntegrationRuntime;

    @Metamodel.attribute({
        display: stringResources.DataFlowComputeTypeLabel,
        constraint: {
            required: true
        },
        templatable: true,
        association: 'COMPUTE_TYPE'
    })
    public computeType?: ComputeType = 'General';

    @Metamodel.attribute({
        display: stringResources.DataFlowCoreCountLabel,
        constraint: {
            required: true,
            minimum: 8,
            whole: true
        },
        templatable: true,
        association: 'CORE_COUNT'
    })
    public coreCount?: number = 8;

    @Metamodel.attribute({
        display: stringResources.SsisActivityLoggingLevelLabel,
        constraint: {
            required: true,
            validator: ExecuteDataFlowActivity.validateTraceLevel
        },
        templatable: true,
        association: 'TRACE_LEVEL'
    })
    public traceLevel?: TraceLevelType = 'Fine';

    @Metamodel.attribute({
        display: stringResources.DataFlowRunConcurrenlty,
        templatable: true,
        association: 'RUN_CONCURRENTLY'
    })
    public runConcurrently?: boolean;

    @Metamodel.attribute({
        display: stringResources.ModelName_DegreeOfParallelism,
        templatable: true,
        association: 'SOURCE_STAGING_CONCURRENCY',
        constraint: {
            required: true,
            validator: ExecuteDataFlowActivity.validateSourceStagingConcurrency
        }
    })
    public sourceStagingConcurrency?: number = 1;

    @Metamodel.attribute({
        display: stringResources.DataFlowContinueOnError,
        templatable: true,
        association: 'CONTINUE_ON_ERROR'
    })
    public continueOnError?: boolean;

    @Metamodel.attribute({
        display: stringResources.DataFlowRunAllowCaching,
        templatable: true,
        association: 'ALLOW_CACHING'
    })
    public allowCaching?: boolean;

    @Metamodel.attribute({
        display: stringResources.LookupActivityConfigurationFirstRowOnly,
        templatable: true,
        association: 'FIRST_ROW_ONLY'
    })
    public firstRowOnly?: boolean;

    @Metamodel.attribute({
        collection: Collection.Array,
        constraint: {
            required: false
        },
        association: 'STAGING_LINKED_SERVICE_PARAMETERS'
    })
    public stagingLinkedServiceParameters: ParameterValue[] = [];

    @Metamodel.attribute({
        collection: Collection.Array,
        constraint: {
            required: false,
            validator: ExecuteDataFlowActivity.validateParameters
        },
        association: 'DATAFLOW_PARAMETERS'
    })
    public dataflowParameters: ParameterValue[] = [];

    @Metamodel.attribute({
        collection: Collection.Set,
        constraint: {
            required: false,
            validator: ExecuteDataFlowActivity.validateDatasetParameters
        },
        association: 'DATAFLOW_DATASET_PARAMETERS'
    })
    public dataflowDatasetParameters: Map<string, ParameterValue[]> = new Map<string, ParameterValue[]>();

    @Metamodel.attribute({
        collection: Collection.Set,
        constraint: {
            required: false,
            validator: ExecuteDataFlowActivity.validateLinkedServiceParameters
        },
        association: 'DATAFLOW_LINKEDSERVICE_PARAMETERS'
    })
    public dataflowLinkedServiceParameters: Map<string, ParameterValue[]> = new Map<string, ParameterValue[]>();

    @Metamodel.attribute({
        collection: Collection.Set,
        constraint: {
            required: false,
            validator: ExecuteDataFlowActivity.validateLinkedServiceParameters
        },
        association: 'DATAFLOW_SCHEMALINKEDSERVICE_PARAMETERS'
    })
    public schemaLinkedServiceParameters: Map<string, ParameterValue[]> = new Map<string, ParameterValue[]>();

    @Metamodel.attribute({
        collection: Collection.Set,
        constraint: {
            required: false,
            validator: ExecuteDataFlowActivity.validateLinkedServiceParameters
        },
        association: 'DATAFLOW_REJECTEDDATALINKEDSERVICE_PARAMETERS'
    })
    public rejectedDataLinkedServiceParameters: Map<string, ParameterValue[]> = new Map<string, ParameterValue[]>();

    @Metamodel.attribute({
        display: stringResources.Dataflow_StagingLinkedService,
        reference: true,
        type: LinkedService.MODEL,
        constraint: {
            validator: ExecuteDataFlowActivity.validateStagingLinkedService
        }
    })
    public stagingLinkedService?: LinkedService;

    @Metamodel.attribute({
        display: stringResources.Dataflow_StagingPath,
        constraint: {
            validator: ExecuteDataFlowActivity.validateStagingPath
        },
        association: 'STAGING_STORAGE_PATH'
    })
    public stagingStoragePath?: string;

    @Metamodel.attribute({
        display: stringResources.DataFlow,
        constraint: {
            required: true
        },
        reference: true,
        type: DataFlow.MODEL
    })
    public dataflow?: DataFlow;

    @Metamodel.attribute({
        constraint: {
            required: false
        }
    })
    public dataFlowDebugSessionId?: string;

    @Metamodel.attribute({
        display: stringResources.CustomizedCheckpointKey,
        constraint: {
            required: false,
            validator: ExecuteDataFlowActivity.validateCustomizedCheckpointKey
        },
        templatable: true,
        association: 'CUSTOMIZED_CHECKPOINT_KEY'
    })
    public customizedCheckpointKey?: string;

    public toggleUsingCustomizedCheckpointKey: boolean = false;
    public alreadyUsingDefaultCheckpointKey: boolean = false;
    public isNewlyAdded: boolean = false;

    public static validateDatasetParameters(dataflowActivity: ExecuteDataFlowActivity): IViolation[] | undefined {
        const violation: IViolation[] = [];
        if (dataflowActivity && dataflowActivity.dataflow) {
            dataflowActivity.dataflow.sources.forEach((source) => {
                violation.push(...validateEntityParameters(source.sourceDataset, dataflowActivity.dataflowDatasetParameters.get(source.name) || [], dataflowActivity, source));
            });
            dataflowActivity.dataflow.sinks.forEach((sink) => {
                violation.push(...validateEntityParameters(sink.sinkDataset, dataflowActivity.dataflowDatasetParameters.get(sink.name) || [], dataflowActivity, sink));
            });
        }
        return violation;
    }


    public static async validateCustomizedCheckpointKey(dataflowActivity: ExecuteDataFlowActivity, value: string, violation: IViolation, context: IValidationContext): Promise<IViolation[] | undefined> {
        const checkpointName = dataflowActivity.customizedCheckpointKey;
        const violations: IViolation[] = [];
        if (!dataflowActivity.toggleUsingCustomizedCheckpointKey) {
            return undefined;
        }

        if (stringIsNullOrEmpty(checkpointName)) {
            violations.push({
                context: dataflowActivity.customizedCheckpointKey,
                message: () => format(stringResources.Validator_NameRequired, stringResources.CustomizedCheckpointKey)
            });
            return violations;
        }

        if (checkpointName.length > ValidationConstants.maxActivityNameLength) {
            violations.push({
                context: dataflowActivity.customizedCheckpointKey,
                message: () => format(stringResources.Validator_NameMaxLength,
                    stringResources.CustomizedCheckpointKey,
                    ValidationConstants.maxActivityNameLength.toString())
            });
        }

        const noSpaceRegex = ValidationConstants.strictlyNoSpaceNameValidator;
        if (!checkpointName.match(noSpaceRegex)) {
            violations.push({
                context: dataflowActivity.customizedCheckpointKey,
                message: () => stringResources.CustomizedCheckpointKey_NoSpaces
            });
        }

        const modelService = await getServiceInstance<ModelService>(context.contextData, 'ModelService');
        return await getAllExistingCheckpoint(dataflowActivity, { modelService }).then((allExistingCheckpoint) => {
            const set = new Set(allExistingCheckpoint);
            if (set.has(dataflowActivity.customizedCheckpointKey)) {
                violations.push({
                    context: dataflowActivity.customizedCheckpointKey,
                    message: () => format(stringResources.DuplicateCheckpointKey_Error, stringResources.CustomizedCheckpointKey)
                });
            }
            return violations.length <= 0 ? undefined : violations;
        }).finally(() => {
            return violations.length <= 0 ? undefined : violations;
        });
    }

    public static validateSourceStagingConcurrency(dataflowActivity: ExecuteDataFlowActivity): IViolation[] | undefined {
        const violation: IViolation[] = [];
        if (dataflowActivity && (dataflowActivity.sourceStagingConcurrency === 0 || dataflowActivity.sourceStagingConcurrency < -1)) {
            violation.push({
                context: dataflowActivity.sourceStagingConcurrency,
                message: () => stringResources.DataflowActivity_SourceStagingConcurrencyError
            });
        }
        return violation.length <= 0 ? undefined : violation;
    }

    public static validateLinkedServiceParameters(dataflowActivity: ExecuteDataFlowActivity): IViolation[] | undefined {
        const violation: IViolation[] = [];
        return violation;
    }

    public static validateParameters(dataflowActivity: ExecuteDataFlowActivity): IViolation[] {
        const violations = dataflowActivity && dataflowActivity.dataflow
            ? validateEntityParameters(dataflowActivity.dataflow, dataflowActivity.dataflowParameters || [], dataflowActivity) : [];
        const dataflowArguments = getDataFlowBaseArguments(dataflowActivity.dataflow);
        dataflowActivity.dataflowParameters.forEach((param) => {
            // if value type is not populated yet (page just refreshed), then populate it.
            checkForEmptyValuetypes(param, dataflowArguments);
            if (param.value && param.parameter instanceof DataFlowParameter) {
                if (!DataFlowTypesUtils.parameterTypeMatch(DataFlowTypesUtils.createType(param.valueType), param.parameter._type)) {
                    violations.push({
                        context: param.parameter,
                        message: () => sanitizeAndFormatString(stringResources.DataFlow_ParameterTypeValidationError, param.parameter.name, param.parameter._type)
                    });
                }
            }
        });
        return violations;
    }

    public static validateTraceLevel(dataflowActivity: ExecuteDataFlowActivity): IViolation[] {
        let cacheSinkOutputSelected = false;
        if (dataflowActivity && dataflowActivity.dataflow) {
            dataflowActivity.dataflow.sinks.forEach((sink) => {
                cacheSinkOutputSelected = cacheSinkOutputSelected || sink.cacheSinkOutput.expression === 'true';
            });
            if (cacheSinkOutputSelected && dataflowActivity.traceLevel !== 'None') {
                return [{
                    context: dataflowActivity,
                    message: () => stringResources.DataFlow_Invalid_TraceLevel_Error
                }];
            }
        }
        return [];
    }

    public static async validateIRConstraints(dataflowActivity: ExecuteDataFlowActivity, value: IntegrationRuntime, violation?: IViolation, context?: IValidationContext): Promise<IViolation[]> {
        return validateIRConstraints(dataflowActivity, dataflowActivity.integrationRuntime, violation, context);
    }

    public static validateStagingLinkedService(dataflowActivity: ExecuteDataFlowActivity): IViolation[] | undefined {
        const stagingRequired = ExecuteDataFlowActivity.isStagingRequired(dataflowActivity);
        const attributeName = ExecuteDataFlowActivity.MODEL.attribute('stagingLinkedService').name;
        return validateStagingLinkedService(dataflowActivity, stagingRequired, attributeName);
    }

    public static validateStagingPath(dataflowActivity: ExecuteDataFlowActivity): IViolation[] | undefined {
        if (ExecuteDataFlowActivity.isStagingRequired(dataflowActivity) && !dataflowActivity.stagingStoragePath) {
            return [{
                context: dataflowActivity,
                message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessage, Metamodel.modelOf(dataflowActivity).display[0],
                    dataflowActivity.name ? '\'' + dataflowActivity.name + '\'' : '', ExecuteDataFlowActivity.MODEL.attribute('stagingStoragePath').name)
            }];
        }
        return undefined;
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    public static isStagingRequired(dataflowActivity: ExecuteDataFlowActivity): boolean {
        if (dataflowActivity && dataflowActivity.dataflow) {
            const sourceRequired = !!dataflowActivity.dataflow.sources.find((source) => {
                return (
                    isSourceTypeEq(adfModelTypeMap.oracle.linkedServiceType, adfModelTypeMap.oracle.datasetType)(source) ||
                    isSapOdpSource(source) ||
                    isSapODataSource(source) ||
                    (source.staged && source.sourceDataset instanceof AzureSqlDWTableDataset) ||
                    (source.datasetSettings instanceof SynapseAnalyticsSettings &&
                        !(source.datasetSettings instanceof HiveSettings) &&
                        source.datasetSettings.staged)
                );
            });
            const sinkRequired = !!dataflowActivity.dataflow.sinks.find((sink) => {
                return (
                    (sink.staged && sink.sinkDataset instanceof AzureSqlDWTableDataset) ||
                    ((sink.datasetSettings instanceof SynapseAnalyticsSettings || sink.datasetSettings instanceof WarehouseSettings) &&
                        !(sink.datasetSettings instanceof HiveSettings) &&
                        sink.datasetSettings.staged)
                );
            });

            return sourceRequired || sinkRequired;
        }
        return false;
    }

    public static async validateExecuteActivity(activity: ExecuteDataFlowActivity, context: IValidationContext): Promise<IViolation[] | undefined> {
        // Avoid checking IR status during a publish, but check during sandbox runs.
        if (!__IS_A365__) {
            let errorMessage: string;

            if (context && !context.isPublish) {
                errorMessage = await isDataflowIRReadyModelValidator(activity.integrationRuntime, context);
            }

            if (!errorMessage) {
                const factory = (await getServiceInstance<FactoryCacheService>(context.contextData, 'FactoryCacheService')).factory;

                const factoryLocation = factory && formatRegionString(factory.location);
                errorMessage = getDataFlowIrSparkGatewayErrorMessage(activity.integrationRuntime, factoryLocation, { exposureControlService: await getServiceInstance<ExposureControlService>(
                    context.contextData,
                    'ExposureControlService'
                ) });
            }

            if (errorMessage) {
                return [{
                    context: activity,
                    doNotAutomaticallyCorrect: true,
                    message: () => errorMessage
                }];
            }
        }
        return undefined;
    }
}
